import React, { useRef, useEffect } from "react";
import "./PassengerSelector.css";
import minus from "../../assets/images/packages/svg/minus.svg";
import plus from "../../assets/images/packages/svg/plus.svg";
import { useTranslation } from "../../contexts/TranslationContext";

const PassengerSelector = ({
  adults,
  children,
  infants,
  onAdultsChange,
  onChildrenChange,
  onInfantsChange,
  onClose,
  style,
  testIdPrefix = "passenger-selector",
}) => {
  const containerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (onClose) onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Functions to handle changes
  const handleAdultChange = (delta) => {
    const newValue = adults + delta;
    if (newValue >= 0) {
      onAdultsChange(newValue);
    }
  };

  const handleChildrenChange = (delta) => {
    const newValue = children + delta;
    if (newValue >= 0) {
      onChildrenChange(newValue);
    }
  };

  const handleInfantsChange = (delta) => {
    const newValue = infants + delta;
    if (newValue >= 0) {
      onInfantsChange(newValue);
    }
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    if (onClose) onClose();
  };

  return (
    <div
      className="passenger-selector-container"
      ref={containerRef}
      onClick={(event) => event.stopPropagation()}
      data-testid={`${testIdPrefix}-container`}
    >
      <div className="passenger-dropdown" style={style}>
        <div className="mb-4">
          {/* Adults */}
          <div className="traveler-counter-container">
            <span className="traveler-name">{t("Adults")}</span>
            <div className="traveler-counter">
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleAdultChange(-1)}
                data-testid={`${testIdPrefix}-adults-decrease`}
              >
                <img src={minus} alt="minus" />
              </button>
              <span data-testid={`${testIdPrefix}-adults-count`}>{adults}</span>
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleAdultChange(1)}
                data-testid={`${testIdPrefix}-adults-increase`}
              >
                <img src={plus} alt="plus" />
              </button>
            </div>
          </div>

          {/* Infants */}
          <div className="traveler-counter-container">
            <div className="traveler-name">
              <span>{t("Infants")}</span>
              <span className="traveler-name-desc">{t("LessThan")} 2</span>
            </div>
            <div className="traveler-counter">
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleInfantsChange(-1)}
                data-testid={`${testIdPrefix}-infants-decrease`}
              >
                <img src={minus} alt="minus" />
              </button>
              <span data-testid={`${testIdPrefix}-infants-count`}>{infants}</span>
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleInfantsChange(1)}
                data-testid={`${testIdPrefix}-infants-increase`}
              >
                <img src={plus} alt="plus" />
              </button>
            </div>
          </div>

          {/* Children */}
          <div className="traveler-counter-container">
            <div className="traveler-name">
              <span>{t("Children")}</span>
              <span className="traveler-name-desc">
                {t("Ages")} 2 {t("To")} 12
              </span>
            </div>
            <div className="traveler-counter">
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChildrenChange(-1)}
                data-testid={`${testIdPrefix}-children-decrease`}
              >
                <img src={minus} alt="minus" />
              </button>
              <span data-testid={`${testIdPrefix}-children-count`}>{children}</span>
              <button
                type="button"
                className="traveler-counter-btn"
                onClick={() => handleChildrenChange(1)}
                data-testid={`${testIdPrefix}-children-increase`}
              >
                <img src={plus} alt="plus" />
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="close-btn"
          onClick={handleClose}
          data-testid={`${testIdPrefix}-done`}
        >
          {t("Done")}
        </button>
      </div>
    </div>
  );
};

export default PassengerSelector;
